/* import __COLOCATED_TEMPLATE__ from './snapshot-item.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import Ember from 'ember';
import { post } from 'embercom/lib/ajax';
import type { Snapshot } from './side-sheet';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import type NotificationsService from 'embercom/services/notifications-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';

interface Signature {
  Args: {
    snapshot: Snapshot;
    workflow: Workflow;
    editorState: EditorState;
    hasDraft: boolean;
    isViewOnly?: boolean;
  };
  Element: HTMLLIElement;
}

export default class SnapshotItem extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intercomConfirmService: IntercomConfirmService;

  get showRollbackButton() {
    return !this.args.isViewOnly && !this.args.snapshot.is_current && !this.args.snapshot.is_draft;
  }

  get isRollbackDisabled() {
    return this.args.hasDraft || taskFor(this.rollbackToSnapshot).isRunning;
  }

  @action
  async confirmRollback() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('workflows.version-history.rollback-confirm-title'),
      body: this.intl.t('workflows.version-history.rollback-confirm-body'),
      confirmButtonText: this.intl.t('workflows.version-history.rollback-confirm-button'),
      cancelButtonText: this.intl.t('workflows.version-history.rollback-cancel-button'),
      primaryButtonType: 'primary-destructive',
    });

    if (isConfirmed) {
      taskFor(this.rollbackToSnapshot).perform();
    }
  }

  @task({ drop: true })
  *rollbackToSnapshot(): TaskGenerator<void> {
    try {
      yield post('/ember/operator_workflows/rollback_to_snapshot', {
        app_id: this.appService.app.id,
        workflow_instance_id: this.args.workflow.workflowInstanceId,
        snapshot_id: this.args.snapshot.id,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('workflows.version-history.rollback-success'),
      );

      if (!Ember.testing) {
        window.location.reload();
      }
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('workflows.version-history.rollback-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::VersionHistory::SnapshotItem': typeof SnapshotItem;
    'workflows/version-history/snapshot-item': typeof SnapshotItem;
  }
}
